/**
 * <s-card-sizes> — Size buttons on a product card. Clicking a size swaps the
 * card's displayed price and photo, and points the card links at that variant,
 * without leaving the listing.
 *
 * Required child markup:
 *   [data-card-price] bdi       — The price text to replace (first <bdi> inside).
 *   button[data-price][data-url] — One per variant; aria-pressed marks the selected one.
 *   Optional on each button: data-image (src) and data-image-srcset — the size's own photo.
 *
 * The closest <article> is the card; its anchors get the selected variant URL
 * and its first <img> gets the selected size's photo.
 */
class SCardSizes extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('click', event => this.#onClick(event), { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #onClick(event) {
    const button = event.target.closest('button[data-price]');
    if (!button || !this.contains(button) || button.disabled) return;
    event.preventDefault();
    event.stopPropagation();

    for (const other of this.querySelectorAll('button[data-price]')) {
      other.setAttribute('aria-pressed', String(other === button));
    }

    const priceText = this.querySelector('[data-card-price] bdi');
    if (priceText) priceText.textContent = button.dataset.price;

    const card = this.closest('article') || this;
    for (const link of card.querySelectorAll('a[href]')) {
      link.setAttribute('href', button.dataset.url);
    }

    // The card's add-to-cart form submits whichever size is selected.
    const variantInput = card.querySelector('input[data-card-variant-input]');
    if (variantInput && button.dataset.variantId) variantInput.value = button.dataset.variantId;

    const image = card.querySelector('img');
    if (image && button.dataset.image) {
      if (button.dataset.imageSrcset) image.setAttribute('srcset', button.dataset.imageSrcset);
      image.setAttribute('src', button.dataset.image);
    }
  }
}

if (!customElements.get('s-card-sizes')) customElements.define('s-card-sizes', SCardSizes);
